import { call, put, takeEvery, takeLatest } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";
import { ApiError } from "@/lib/apiClient";
import {
  // Existing imports
  fetchWholesalerFailure,
  fetchWholesalerRequest,
  fetchWholesalerSuccess,
  // Enhanced imports
  fetchWholesalersRequest,
  fetchWholesalersSuccess,
  fetchWholesalersFailure,
  searchWholesalersRequest,
  searchWholesalersSuccess,
  searchWholesalersFailure,
  fetchWholesalerProductsRequest,
  fetchWholesalerProductsSuccess,
  fetchWholesalerProductsFailure,
  searchWholesalerProductsRequest,
  searchWholesalerProductsSuccess,
  searchWholesalerProductsFailure,
  fetchWholesalerCollectionsRequest,
  fetchWholesalerCollectionsSuccess,
  fetchWholesalerCollectionsFailure,
  searchWholesalerCollectionsRequest,
  searchWholesalerCollectionsSuccess,
  searchWholesalerCollectionsFailure,
  importProductsRequest,
  importProductsSuccess,
  importProductsFailure,
  importCollectionsRequest,
  importCollectionsSuccess,
  importCollectionsFailure,
  unmapProductsRequest,
  unmapProductsSuccess,
  unmapProductsFailure,
  unmapCollectionsRequest,
  unmapCollectionsSuccess,
  unmapCollectionsFailure,
} from "../slices/wholesaler.slice";
import {
  // Enhanced service imports
  getWholesalers,
  getWholesalerById,
  searchWholesalers,
  getWholesalerProducts,
  searchWholesalerProducts,
  getWholesalerCollections,
  searchWholesalerCollections,
  importProductsFromWholesaler,
  importCollectionsFromWholesaler,
  unmapProductsFromWholesaler,
  unmapCollectionsFromWholesaler,
  FetchWholesalersOptions,
  SearchWholesalersOptions,
  FetchWholesalerProductsOptions,
  SearchWholesalerProductsOptions,
  FetchWholesalerCollectionsOptions,
  SearchWholesalerCollectionsOptions,
  ImportProductsRequest,
  ImportCollectionsRequest,
  UnmapProductsRequest,
  UnmapCollectionsRequest,
} from "@/services/wholesalerService";

// Note: single-wholesaler fetch handled by fetchSingleWholesalerSaga below

// Enhanced Saga Functions

// Fetch Wholesalers Saga (Enhanced)
function* fetchWholesalersSaga(
  action: PayloadAction<FetchWholesalersOptions>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(getWholesalers, action.payload);

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      fetchWholesalersSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchWholesalersFailure(error));
    } else {
      yield put(
        fetchWholesalersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Single Wholesaler Saga (Enhanced)
function* fetchSingleWholesalerSaga(
  action: PayloadAction<{ wholesalerId: string }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId } = action.payload;
    const response: any = yield call(getWholesalerById, wholesalerId);

    yield put(fetchWholesalerSuccess({ wholesaler: response }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchWholesalerFailure(error));
    } else {
      yield put(
        fetchWholesalerFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Wholesalers Saga
function* searchWholesalersSaga(
  action: PayloadAction<SearchWholesalersOptions>,
): Generator<any, void, any> {
  try {
    const response: any = yield call(searchWholesalers, action.payload);

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      searchWholesalersSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchWholesalersFailure(error));
    } else {
      yield put(
        searchWholesalersFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Wholesaler Products Saga
function* fetchWholesalerProductsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    options: FetchWholesalerProductsOptions;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, options } = action.payload;
    const response: any = yield call(
      getWholesalerProducts,
      wholesalerId,
      options,
    );

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      fetchWholesalerProductsSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchWholesalerProductsFailure(error));
    } else {
      yield put(
        fetchWholesalerProductsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Wholesaler Products Saga
function* searchWholesalerProductsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    options: SearchWholesalerProductsOptions;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, options } = action.payload;
    const response: any = yield call(
      searchWholesalerProducts,
      wholesalerId,
      options,
    );

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      searchWholesalerProductsSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchWholesalerProductsFailure(error));
    } else {
      yield put(
        searchWholesalerProductsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Fetch Wholesaler Collections Saga
function* fetchWholesalerCollectionsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    options: FetchWholesalerCollectionsOptions;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, options } = action.payload;
    const response: any = yield call(
      getWholesalerCollections,
      wholesalerId,
      options,
    );

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      fetchWholesalerCollectionsSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(fetchWholesalerCollectionsFailure(error));
    } else {
      yield put(
        fetchWholesalerCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Search Wholesaler Collections Saga
function* searchWholesalerCollectionsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    options: SearchWholesalerCollectionsOptions;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, options } = action.payload;
    const response: any = yield call(
      searchWholesalerCollections,
      wholesalerId,
      options,
    );

    const list = response.data || response.list || [];
    const page = response.pagination?.page ?? response.page ?? 1;
    const total = response.pagination?.total ?? response.total ?? 0;
    const pageSize =
      response.pagination?.pageSize ?? response.pagination?.page_size ?? response.pageSize ?? 10;
    const totalPages =
      response.pagination?.totalPages ?? response.pagination?.total_pages ?? response.totalPages ?? 1;

    yield put(
      searchWholesalerCollectionsSuccess({
        list,
        page,
        total,
        pageSize,
        totalPages,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(searchWholesalerCollectionsFailure(error));
    } else {
      yield put(
        searchWholesalerCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Import Products Saga
function* importProductsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    request: ImportProductsRequest;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, request } = action.payload;
    const response: any = yield call(
      importProductsFromWholesaler,
      wholesalerId,
      request,
    );

    yield put(
      importProductsSuccess({
        imported: response.imported,
        updated: response.updated,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(importProductsFailure(error));
    } else {
      yield put(
        importProductsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Import Collections Saga
function* importCollectionsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    request: ImportCollectionsRequest;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, request } = action.payload;
    const response: any = yield call(
      importCollectionsFromWholesaler,
      wholesalerId,
      request,
    );

    yield put(
      importCollectionsSuccess({
        imported: response.imported,
        updated: response.updated,
      }),
    );
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(importCollectionsFailure(error));
    } else {
      yield put(
        importCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Unmap Products Saga
function* unmapProductsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    request: UnmapProductsRequest;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, request } = action.payload;
    const response: any = yield call(
      unmapProductsFromWholesaler,
      wholesalerId,
      request,
    );

    yield put(unmapProductsSuccess({ unmapped: response.unmapped }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(unmapProductsFailure(error));
    } else {
      yield put(
        unmapProductsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

// Unmap Collections Saga
function* unmapCollectionsSaga(
  action: PayloadAction<{
    wholesalerId: string;
    request: UnmapCollectionsRequest;
  }>,
): Generator<any, void, any> {
  try {
    const { wholesalerId, request } = action.payload;
    const response: any = yield call(
      unmapCollectionsFromWholesaler,
      wholesalerId,
      request,
    );

    yield put(unmapCollectionsSuccess({ unmapped: response.unmapped }));
  } catch (error: any) {
    if (error instanceof ApiError) {
      yield put(unmapCollectionsFailure(error));
    } else {
      yield put(
        unmapCollectionsFailure(
          new ApiError(
            500,
            "UNKNOWN_ERROR",
            error?.message || "An unexpected error occurred",
          ),
        ),
      );
    }
  }
}

export default function* wholesalerRootSaga() {
  // Existing saga - single wholesaler fetch
  yield takeLatest(fetchWholesalerRequest.type, fetchSingleWholesalerSaga);

  // Enhanced sagas
  yield takeLatest(fetchWholesalersRequest.type, fetchWholesalersSaga);
  yield takeLatest(searchWholesalersRequest.type, searchWholesalersSaga);
  yield takeLatest(
    fetchWholesalerProductsRequest.type,
    fetchWholesalerProductsSaga,
  );
  yield takeLatest(
    searchWholesalerProductsRequest.type,
    searchWholesalerProductsSaga,
  );
  yield takeLatest(
    fetchWholesalerCollectionsRequest.type,
    fetchWholesalerCollectionsSaga,
  );
  yield takeLatest(
    searchWholesalerCollectionsRequest.type,
    searchWholesalerCollectionsSaga,
  );
  yield takeEvery(importProductsRequest.type, importProductsSaga);
  yield takeEvery(importCollectionsRequest.type, importCollectionsSaga);
  yield takeEvery(unmapProductsRequest.type, unmapProductsSaga);
  yield takeEvery(unmapCollectionsRequest.type, unmapCollectionsSaga);
}
